<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue';
import Icon from './Icon.vue';

const props = defineProps<{ title?: string; width?: number; subtitle?: string }>();
const open = defineModel<boolean>({ default: false });

const onKey = (e: KeyboardEvent) => {
  if (e.key === 'Escape') open.value = false;
};
watch(
  open,
  (v) => {
    if (v) window.addEventListener('keydown', onKey);
    else window.removeEventListener('keydown', onKey);
  },
  { immediate: true },
);
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));
void props;
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="open" class="overlay" @mousedown.self="open = false">
        <div class="modal" :style="{ width: `min(${width ?? 520}px, calc(100vw - 32px))` }" role="dialog" aria-modal="true">
          <header v-if="title" class="head">
            <div class="grow">
              <h2>{{ title }}</h2>
              <p v-if="subtitle" class="muted small">{{ subtitle }}</p>
            </div>
            <button class="x" type="button" aria-label="Закрыть" @click="open = false"><Icon name="close" /></button>
          </header>
          <div class="body"><slot /></div>
          <footer v-if="$slots.footer" class="foot"><slot name="footer" /></footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: var(--overlay);
  backdrop-filter: blur(3px);
  display: grid;
  place-items: center;
  padding: 16px;
}
.modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
}
.head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 0;
}
.body {
  padding: 16px 20px 20px;
  overflow: auto;
}
.foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid var(--divider);
}
.x {
  border: none;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  border-radius: 6px;
  padding: 4px;
}
.x:hover {
  background: var(--surface-hover);
  color: var(--text);
}
.modal-enter-active,
.modal-leave-active {
  transition: opacity 160ms var(--ease);
}
.modal-enter-active .modal,
.modal-leave-active .modal {
  transition: transform 180ms var(--ease);
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .modal,
.modal-leave-to .modal {
  transform: translateY(8px) scale(0.98);
}
</style>
